import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ChevronDown, Shirt, Palette, Ruler, Truck, HeartHandshake,
  ShieldCheck, Boxes, Pencil, Settings, Star,
} from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ASSETS } from "@/lib/assets";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const CATEGORIES = [
  "All Products", "Custom Apparel", "Tumblers & Drinkware", "Personalized Gifts",
  "Home & Living", "Digital Products", "Seasonal Collections", "Resin Art",
];

const HERO_FEATURES = [
  { Icon: Shirt, label: "High Quality Prints" },
  { Icon: Palette, label: "Personalized Designs" },
  { Icon: Ruler, label: "All Sizes S–5XL" },
  { Icon: Truck, label: "Fast Turnaround" },
  { Icon: HeartHandshake, label: "Great Customer Service" },
];

const FILTERS = [
  { name: "Size", options: ["S", "M", "L", "XL", "2XL", "3XL"] },
  { name: "Color", options: ["Black", "White", "Navy", "Royal Blue", "Gray"] },
  { name: "Design Type", options: ["Faith", "Statement", "Scripture", "Minimal"] },
  { name: "Price", options: ["Under $25", "$25 – $35", "$35+"] },
];

export default function Shop() {
  useSeo("Collins Crafty Creations — Custom Apparel, Tumblers, Gifts & More | Memphis, TN", "Shop Collins Crafty Creations: custom apparel, tumblers & drinkware, personalized gifts, home & living, digital products, seasonal collections, and resin art. Crafted with care in Memphis, TN.");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All Products");
  const [sort, setSort] = useState("featured");
  const [detail, setDetail] = useState(null);
  const [fSizes, setFSizes] = useState([]);
  const [fColors, setFColors] = useState([]);
  const [fPriceMax, setFPriceMax] = useState(100);
  const [fSeasonal, setFSeasonal] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (setter, val) => setter((arr) => arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  const clearFilters = () => { setFSizes([]); setFColors([]); setFPriceMax(100); setFSeasonal(false); setCat("All Products"); };
  const filtersActive = fSizes.length || fColors.length || fSeasonal || fPriceMax < 100 || cat !== "All Products";

  let list = cat === "All Products" ? [...products] : products.filter((p) => p.category === cat);
  if (fSeasonal) list = list.filter((p) => p.seasonal);
  if (fPriceMax < 100) list = list.filter((p) => p.price <= fPriceMax);
  if (fSizes.length) list = list.filter((p) => (p.sizes || []).some((s) => fSizes.includes(s.size) && s.stock > 0));
  if (fColors.length) list = list.filter((p) => (p.colors || []).some((c) => fColors.includes(c.name) && c.stock > 0));
  if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
  else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
  else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
  else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div data-testid="shop-page">
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-silver-light">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-2 text-xs text-navy/60" data-testid="shop-breadcrumb">
          <Link to="/" className="hover:text-navy-royal">Home</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="hover:text-navy-royal">Collins Crafty Creations</Link>
          <ChevronRight size={12} />
          <span className="text-navy-dark font-semibold">Custom Apparel</span>
        </div>
      </div>

      {/* Sub-brand hero */}
      <section className="relative overflow-hidden bg-navy-dark text-white" data-testid="shop-hero">
        <div className="max-w-[1500px] mx-auto px-6 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-script text-4xl sm:text-5xl text-white">Collins Crafty Creations</div>
            <div className="text-xs tracking-[0.3em] uppercase text-white/70 font-semibold mt-2">Crafted With Care</div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-none">Custom Apparel</h1>
            <div className="text-sm tracking-[0.35em] uppercase text-white/80 mt-3">Wear Your Story</div>
            <p className="text-white/80 mt-5 max-w-lg leading-relaxed">
              More than just shirts — custom apparel designed to inspire, express, and make a statement.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              {HERO_FEATURES.map((f) => (
                <div key={f.label} className="flex flex-col items-center text-center w-24" data-testid={`hero-feature-${f.label}`}>
                  <div className="w-12 h-12 rounded-full border-2 border-white/40 flex items-center justify-center"><f.Icon size={20} /></div>
                  <div className="text-[10px] uppercase tracking-wide text-white/80 mt-2 leading-tight font-semibold">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={ASSETS.craftyHero} alt="Custom faith apparel" className="w-full h-72 lg:h-96 object-cover rounded-2xl" />
            <div className="absolute top-4 right-4 bg-navy-dark/85 backdrop-blur rounded-xl p-4 text-right hidden sm:block">
              <div className="text-xs font-bold uppercase tracking-wide text-white leading-tight">Custom Apparel<br />For Every Occasion</div>
              <ul className="mt-2 text-[11px] text-white/80 space-y-0.5">
                <li>Individuals</li><li>Families</li><li>Teams</li><li>Businesses</li><li>Events & More!</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Storefront */}
      <SeasonalBanner products={products} onShop={() => { setFSeasonal(true); document.getElementById("shop-product-area")?.scrollIntoView({ behavior: "smooth" }); }} />
      <div className="max-w-[1500px] mx-auto px-6 py-14 grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-8" data-testid="shop-sidebar">
          <div>
            <h3 className="font-serif text-2xl text-navy-dark border-b-2 border-navy-royal pb-2">Shop by Category</h3>
            <ul className="mt-4 space-y-1">
              {CATEGORIES.map((c) => (
                <li key={c}>
                  <button onClick={() => setCat(c)} data-testid={`cat-${c}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${cat === c ? "bg-navy-dark text-white font-semibold" : "text-navy/75 hover:bg-slate-100"}`}>
                    {cat === c && <ChevronRight size={13} />}{c}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center justify-between border-b-2 border-navy-royal pb-2">
              <h3 className="font-serif text-2xl text-navy-dark">Filter By</h3>
              {filtersActive ? <button onClick={clearFilters} className="text-xs text-navy-royal font-semibold hover:underline" data-testid="clear-filters">Clear</button> : null}
            </div>
            <div className="mt-4 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold mb-2">Size</div>
                <div className="flex flex-wrap gap-1.5">
                  {["S","M","L","XL","2XL","3XL","4XL","5XL"].map((s) => (
                    <button key={s} onClick={() => toggle(setFSizes, s)} data-testid={`fsize-${s}`}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${fSizes.includes(s) ? "bg-navy-dark text-white border-navy-dark" : "border-silver-light text-navy/70 hover:border-navy-royal"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold mb-2">Color</div>
                <div className="flex flex-wrap gap-2">
                  {[{n:"Black",h:"#1a1a1a"},{n:"White",h:"#f4f4f5"},{n:"Navy",h:"#0c2340"},{n:"Royal Blue",h:"#1044a5"},{n:"Gray",h:"#9ca3af"},{n:"Red",h:"#b91c1c"}].map((c) => (
                    <button key={c.n} onClick={() => toggle(setFColors, c.n)} title={c.n} data-testid={`fcolor-${c.n}`}
                      className={`w-7 h-7 rounded-full border-2 ${fColors.includes(c.n) ? "border-navy-royal ring-2 ring-navy-royal/30" : "border-silver-light"}`} style={{ background: c.h }} />
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs uppercase tracking-wide text-navy/50 font-semibold mb-1"><span>Max Price</span><span className="text-navy-dark">${fPriceMax}</span></div>
                <input type="range" min="10" max="100" step="5" value={fPriceMax} onChange={(e) => setFPriceMax(Number(e.target.value))} className="w-full accent-navy-royal" data-testid="fprice" />
              </div>
              <label className="flex items-center gap-2 text-sm text-navy/75 cursor-pointer">
                <input type="checkbox" checked={fSeasonal} onChange={(e) => setFSeasonal(e.target.checked)} className="accent-crafty" data-testid="fseasonal" />
                Seasonal items only
              </label>
            </div>
          </div>

          <div className="rounded-2xl bg-slate-50 border border-silver-light p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-navy-royal/10 text-navy-royal flex items-center justify-center mx-auto"><Shirt size={22} /></div>
            <div className="font-serif text-xl text-navy-dark mt-3 leading-tight">Have a Custom Idea?<br />We've Got You!</div>
            <p className="text-xs text-navy/60 mt-2">Send your idea, logo, or inspiration and we'll bring it to life.</p>
            <a href="#custom-order" className="mt-4 inline-block rounded-md bg-navy-dark text-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-navy-royal transition-colors" data-testid="sidebar-custom-order">
              Request a Custom Order
            </a>
          </div>
        </aside>

        {/* Product area */}
        <div className="lg:col-span-9" data-testid="shop-product-area">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-silver-light pb-6">
            <div>
              <h2 className="font-serif text-4xl text-navy-dark">{cat === "All Products" ? "All Products" : cat}</h2>
              <p className="text-navy/60 text-sm mt-1">From everyday looks to special occasions, our custom apparel is designed just for you.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-navy/50 font-medium hidden sm:inline" data-testid="result-count">Showing {list.length} of {(cat === "All Products" ? products : products.filter((p) => p.category === cat)).length}</span>
              <span className="text-xs uppercase tracking-wide text-navy/50 font-semibold">Sort By</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-40 rounded-md" data-testid="shop-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="name">Name A–Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="animate-pulse aspect-[4/5] bg-slate-100 rounded-xl" />)}
            </div>
          ) : list.length === 0 ? (
            <div className="mt-16 text-center py-16 bg-slate-50 rounded-2xl border border-silver-light" data-testid="shop-empty">
              <Shirt size={40} className="mx-auto text-navy/30" />
              <div className="font-serif text-2xl text-navy-dark mt-4">No products match your filters</div>
              <p className="text-navy/60 mt-2 text-sm">Try adjusting your filters — or let us make exactly what you're picturing.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button onClick={clearFilters} className="rounded-md border border-navy-dark text-navy-dark px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-navy-dark hover:text-white transition-colors" data-testid="empty-clear">Clear Filters</button>
                <a href="#custom-order" className="rounded-md bg-navy-dark text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-navy-royal transition-colors" data-testid="empty-custom">Request a Custom Order</a>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-10" data-testid="shop-grid">
              <AnimatePresence mode="popLayout">
                {list.map((p) => (
                  <motion.div key={p.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}>
                    <ProductCard product={p} onView={() => setDetail(p)} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Custom order banner */}
      <CustomOrderSection />

      {/* Service guarantee bar */}
      <section className="border-t border-silver-light bg-white" data-testid="shop-guarantees">
        <div className="max-w-[1500px] mx-auto px-6 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { Icon: Truck, t: "Fast Turnaround", body: "Get your custom items when you need them." },
            { Icon: ShieldCheck, t: "High Quality", body: "Long-lasting prints you'll love." },
            { Icon: HeartHandshake, t: "Great Customer Service", body: "We're here to help." },
            { Icon: Boxes, t: "Bulk Orders Welcome", body: "Perfect for teams, businesses, organizations, and events." },
          ].map((g, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`guarantee-${i}`}>
              <div className="text-navy-royal shrink-0"><g.Icon size={26} /></div>
              <div>
                <div className="font-serif text-lg text-navy-dark leading-tight">{g.t}</div>
                <div className="text-xs text-navy/60 mt-1">{g.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Detail / add-to-cart dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-white p-0 max-h-[92vh] overflow-y-auto">
          {detail && <ProductDetail product={detail} onAdd={add} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProductDetail({ product, onAdd, onClose }) {
  const sizes = product.sizes || [];
  const colors = product.colors || [];
  const [size, setSize] = useState(() => (sizes.find((s) => s.stock > 0)?.size || null));
  const [color, setColor] = useState(() => (colors.find((c) => c.stock > 0)?.name || null));
  const sizeObj = sizes.find((s) => s.size === size);
  const price = product.price + (sizeObj?.upcharge || 0);
  const needsSize = sizes.length > 0;
  const needsColor = colors.length > 0;
  const canAdd = (!needsSize || size) && (!needsColor || color);

  const addToCart = () => {
    onAdd({ ...product, price, size: size || undefined, color: color || undefined });
    onClose();
    toast.success(`${product.name}${size ? ` (${size}${color ? `, ${color}` : ""})` : ""} added to cart`);
  };

  return (
    <div className="grid md:grid-cols-2">
      <img src={product.image_url} alt={product.name} className="w-full h-auto md:h-full max-h-[38vh] md:max-h-[560px] object-cover" />
      <div className="p-8 flex flex-col md:max-h-[80vh] md:overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-navy-dark leading-tight text-left">{product.name}</DialogTitle>
          <DialogDescription className="sr-only">Choose size and color, then add to cart</DialogDescription>
        </DialogHeader>
        <div className="text-navy-royal font-semibold text-xl mt-2" data-testid="detail-price">${price.toFixed(2)}{sizeObj?.upcharge ? <span className="text-xs text-navy/50 font-normal ml-1">(+${sizeObj.upcharge.toFixed(2)} {size})</span> : null}</div>
        <div className="flex items-center gap-1 mt-2">
          {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className="fill-navy-royal text-navy-royal" />)}
          <span className="text-xs text-navy/60 ml-2">Loved by Memphis</span>
        </div>
        <p className="text-navy/70 mt-4 leading-relaxed text-sm">{product.description}</p>

        {needsSize && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold mb-2">Size</div>
            <div className="flex flex-wrap gap-2">
              {sizes.map((s) => {
                const oos = s.stock <= 0;
                return (
                  <button key={s.size} disabled={oos} onClick={() => setSize(s.size)} data-testid={`size-${s.size}`}
                    className={`relative px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${oos ? "border-silver-light text-navy/30 line-through cursor-not-allowed" : size === s.size ? "bg-navy-dark text-white border-navy-dark" : "border-silver-light text-navy/75 hover:border-navy-royal"}`}>
                    {s.size}{s.upcharge > 0 && !oos ? <span className="text-[10px] align-super ml-0.5">+${s.upcharge}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {needsColor && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold mb-2">Color{color ? `: ${color}` : ""}</div>
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => {
                const oos = c.stock <= 0;
                return (
                  <button key={c.name} disabled={oos} onClick={() => setColor(c.name)} title={c.name} data-testid={`color-${c.name}`}
                    className={`relative w-9 h-9 rounded-full border-2 ${oos ? "opacity-40 cursor-not-allowed" : color === c.name ? "border-navy-royal ring-2 ring-navy-royal/30" : "border-silver-light"}`} style={{ background: c.hex }}>
                    {oos && <span className="absolute inset-0 flex items-center justify-center text-cpr text-lg leading-none">✕</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-auto pt-6">
          <Button onClick={addToCart} disabled={!canAdd}
            className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 font-bold uppercase tracking-[0.15em] text-xs disabled:opacity-50"
            data-testid={`add-to-cart-${product.id}`}>
            {canAdd ? `Add to Cart — $${price.toFixed(2)}` : "Select options"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ name, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-1">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-navy-dark" data-testid={`filter-${name}`}>
        {name} <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="pb-3 space-y-2">
              {options.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
                  <input type="checkbox" className="accent-navy-royal" /> {o}
                </label>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SeasonalBanner({ products, onShop }) {
  const seasonal = (products || []).find((p) => p.seasonal && p.season_end);
  const end = seasonal?.season_end ? new Date(seasonal.season_end).getTime() : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  if (!seasonal || !end || end <= now) return null;
  const ms = end - now;
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  const seasonalCount = products.filter((p) => p.seasonal).length;
  return (
    <section className="max-w-[1500px] mx-auto px-6 pt-8" data-testid="seasonal-banner">
      <div className="rounded-2xl overflow-hidden relative text-white p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6"
        style={{ background: "linear-gradient(120deg,#0c2340,#1044a5)" }}>
        <div className="absolute top-0 right-0 w-40 h-40 opacity-20" style={{ background: "radial-gradient(circle,#d4af37,transparent 70%)" }} />
        <div className="relative">
          <div className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full" style={{ background: "linear-gradient(135deg,#b8860b,#d4af37)" }}>Seasonal Spotlight</div>
          <h3 className="font-serif text-3xl sm:text-4xl mt-3">{seasonal.season || "Current Season Exclusive"}</h3>
          <p className="text-white/80 text-sm mt-1">{seasonalCount} limited-edition pieces — available for a limited time.</p>
        </div>
        <div className="relative flex flex-col items-center gap-3">
          <div className="flex items-center gap-2" data-testid="countdown">
            {[["Days", d], ["Hrs", h], ["Min", m]].map(([l, v]) => (
              <div key={l} className="bg-white/10 backdrop-blur rounded-lg px-3 py-2 text-center min-w-[56px]">
                <div className="font-serif text-2xl">{v}</div><div className="text-[10px] uppercase tracking-wide text-white/70">{l}</div>
              </div>
            ))}
          </div>
          <button onClick={onShop} className="rounded-md bg-white text-navy-dark px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-crafty hover:text-white transition-colors" data-testid="shop-seasonal">Shop the Collection</button>
        </div>
      </div>
    </section>
  );
}

function CustomOrderSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", project_type: "Custom Apparel", quantity: 1, deadline: "", details: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please sign in or create an account to submit a custom order"); setOpen(false); navigate("/account?next=/shop"); return; }
    setSubmitting(true);
    try {
      await api.post("/custom-orders", { ...form, quantity: Number(form.quantity) });
      setSubmitted(true);
      toast.success("Custom order request received!");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="custom-order" className="max-w-[1500px] mx-auto px-6 py-8 scroll-mt-24" data-testid="custom-order-section">
      <div className="relative overflow-hidden rounded-3xl bg-navy-dark text-white">
        <div className="relative p-10 sm:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-script text-4xl sm:text-5xl text-white leading-tight">Custom Apparel That Speaks to You</div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/shop/studio" data-testid="open-studio" className="inline-flex items-center rounded-md bg-white text-navy-dark hover:bg-navy-royal hover:text-white px-8 py-4 font-bold uppercase tracking-[0.15em] text-xs transition-colors">
                Start Your Custom Order <ChevronRight size={16} className="ml-1" />
              </Link>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="open-custom-order" className="text-white/80 underline text-sm hover:text-white">or send a quick request</button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">Custom Order Request</DialogTitle></DialogHeader>
                  {submitted ? (
                    <div className="p-4 rounded-xl bg-navy-royal/5 border border-navy-royal/20 text-center" data-testid="custom-success">
                      <div className="font-serif text-xl text-navy-dark">Thanks!</div>
                      <div className="text-sm text-navy/70 mt-1">We'll get back to you with a quote within one business day.</div>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="custom-form">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Name</Label><Input required value={form.full_name} onChange={update("full_name")} data-testid="co-name" /></div>
                        <div><Label>Email</Label><Input required type="email" value={form.email} onChange={update("email")} data-testid="co-email" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Phone</Label><Input value={form.phone} onChange={update("phone")} data-testid="co-phone" /></div>
                        <div><Label>Quantity</Label><Input required type="number" min={1} value={form.quantity} onChange={update("quantity")} data-testid="co-qty" /></div>
                      </div>
                      <div><Label>Project type</Label><Input required value={form.project_type} onChange={update("project_type")} data-testid="co-type" /></div>
                      <div><Label>Deadline (optional)</Label><Input type="date" value={form.deadline} onChange={update("deadline")} data-testid="co-deadline" /></div>
                      <div><Label>Tell us about your project</Label><Textarea required rows={4} value={form.details} onChange={update("details")} data-testid="co-details" /></div>
                      <Button type="submit" disabled={submitting} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="co-submit">
                        {submitting ? "Sending…" : "Send request"}
                      </Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 lg:justify-self-end">
            {[{ Icon: Pencil, t: "Your Idea" }, { Icon: Settings, t: "Our Design" }, { Icon: Shirt, t: "Your Apparel" }].map((s) => (
              <div key={s.t} className="flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-full border-2 border-white/40 flex items-center justify-center"><s.Icon size={22} /></div>
                <div className="text-xs uppercase tracking-wide text-white/85 font-semibold">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
